import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { DTMBarChart } from "@/components/charts/DTMBarChart";
import { DTMComparisonBarChart } from "@/components/charts/DTMComparisonBarChart";
import { ToolCTA, InlineNewsletter, ShareBar, RelatedResearch } from "@/components/InlineCTA";

/* ── Chart Data ─────────────────────────────────────────── */

const CHART_A_DATA = [
  { label: "0 trades", value: 7.74 },
  { label: "1–2", value: 8.03 },
  { label: "3–5", value: 9.15 },
  { label: "6–10", value: 9.26 },
  { label: "10+", value: 9.37, highlight: true },
];

const CHART_B_DATA = [
  { label: "Early (1–4)", seriesA: 20.35, seriesB: 20.18 },
  { label: "Mid (5–9)", seriesA: 24.12, seriesB: 24.89 },
  { label: "Late (10–13)", seriesA: 16.45, seriesB: 16.92 },
  { label: "Playoff (14–18)", seriesA: 6.95, seriesB: 4.14 },
  { label: "Offseason", seriesA: 32.13, seriesB: 33.87 },
];

const CHART_C_DATA = [
  { label: ">75% gap", seriesA: 9.52, seriesB: 7.29 },
  { label: ">85% gap", seriesA: 9.67, seriesB: 7.02 },
  { label: ">96% gap", seriesA: 10.01, seriesB: 6.68 },
];

/* ── FAQ Schema ─────────────────────────────────────────── */

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Do teams that trade more win fantasy football?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A little. In our data of 167,265 real dynasty trades, championship teams averaged 3.07 trades versus 2.76 for the field. The championship rate climbs from 7.74% for teams that never trade to 9.37% for the busiest traders. Trading helps, but the climb is modest.",
      },
    },
    {
      "@type": "Question",
      name: "What do winning fantasy football teams do differently?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "They trade at the right time. Champions made 6.95% of their trades in the playoff weeks (14-18), versus just 4.14% for the field — roughly 68% more active when it counts.",
      },
    },
    {
      "@type": "Question",
      name: "Should I try to win every dynasty trade?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Surprisingly, no. In the most lopsided trades, the side that 'lost' on value won the championship more often (10.0%) than the side that 'won' it (6.7%). Overpaying for the right player is often a title-winning move.",
      },
    },
    {
      "@type": "Question",
      name: "How do I tell if a dynasty trade is good?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It should improve your starting lineup or your long-term value and fit your window. Consolidating two good players into one great starter is often the highest-value move, even when it looks even on a calculator.",
      },
    },
  ],
};

/* ── Stat Card ──────────────────────────────────────────── */

function StatCard({ label, value }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-3 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="font-mono text-xl font-bold text-white">{value}</p>
      <p className="text-[9px] font-bold uppercase text-[#555] mt-1">{label}</p>
    </div>
  );
}

/* ── Blockquote / Example Trade ─────────────────────────── */

function TradeExample({ children }) {
  return (
    <blockquote className="border-l-2 border-[#ccff00] pl-4 my-5 bg-[#ccff00]/5 py-3 pr-4 rounded-r-sm">
      <p className="text-sm text-[#d4d4d4] leading-relaxed italic">{children}</p>
    </blockquote>
  );
}

/* ── Main Component ─────────────────────────────────────── */

export default function TradeMoreWinResearch() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="trade-more-win-research">
      <SEO
        title="Do Teams That Trade More Win Fantasy Football? We Checked 167,000 Real Trades — Dynasty Trade Maker"
        description="We pulled 167,265 trades from 6,976 dynasty leagues. Championship teams don't trade more — they trade smarter. Here's what the data says about trading and winning."
        path="/research/do-teams-that-trade-more-win"
        image="https://dynastytrademaker.com/hero-images/do-teams-that-trade-more-win.jpg"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <PublicNav />

      <article className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Link to="/research" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Research</Link>

        {/* Header */}
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2">167,265 REAL TRADES ANALYZED</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="article-title">

        {/* Hero Image */}
        <div className="my-6 border border-[#262626] rounded-sm overflow-hidden" data-testid="hero-image">
          <img src="/hero-images/do-teams-that-trade-more-win.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>
        <ShareBar title="Do Teams That Trade More Win Fantasy Football?" path="/research/do-teams-that-trade-more-win" />
          DO TEAMS THAT TRADE MORE <span className="text-[#ccff00]">WIN FANTASY FOOTBALL?</span>
        </h1>
        <p className="text-base text-[#a3a3a3] mb-8">
          We pulled 167,265 trades from 6,976 completed Sleeper dynasty leagues — every one a league that crowned a champion — and compared what the winners did against everyone else. The answer is not quite what either side of the argument expects.
        </p>
        <p className="text-sm font-semibold text-white mb-10">
          Trading more helps a little. But it's not how much champions trade that sets them apart. It's <span className="text-[#ccff00]">when</span>.
        </p>


        <div className="space-y-10 text-sm text-[#a3a3a3] leading-relaxed">

          {/* ── The Myth ────────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE MYTH: MORE TRADES, MORE RINGS</h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <StatCard label="Trades Analyzed" value="167,265" />
              <StatCard label="Champ Avg Trades" value="3.07" />
              <StatCard label="Field Avg Trades" value="2.76" />
              <StatCard label="Champ Median" value="1" />
            </div>

            <p className="mb-4">
              Championship teams averaged <strong className="text-white">3.07 trades</strong> across a season. Everyone else averaged 2.76. That's a difference of about a third of a trade. The median for both groups was the same: <strong className="text-white">one trade</strong>. So the typical champion and the typical also-ran traded almost identically.
            </p>
            <p className="mb-5">
              Trade volume does nudge your odds. If you bucket teams by how often they traded, the championship rate climbs steadily: teams that made zero trades won 7.74% of the time, 1–2 trades bumped it to 8.03%, and the busiest teams (10+) topped out at 9.37%. Going from never trading to trading constantly bought you <strong className="text-white">under two percentage points</strong> of championship rate.
            </p>

            {/* CHART A */}
            <DTMBarChart
              data={CHART_A_DATA}
              title="Championship Rate by Trade Volume"
              subtitle="% of teams in each bucket that won a title | ALL formats"
              source="DTM Master Data Export — A1.csv | 120,296 team-seasons"
              layout="horizontal"
              height={240}
              formatter={(v) => `${v}%`}
              domain={[6, 10]}
              defaultColor="#333"
            />

            <p className="mt-5">
              Read that honestly and the takeaway is freeing: trading a little is better than not trading, but you don't have to be the league's most active manager to win. The wheeler-dealer reputation is mostly vibes. Volume helps at the margins, not at the core.
            </p>
          </section>

          {/* ── The Real Edge ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE REAL EDGE: CHAMPIONS TRADE <span className="text-[#ccff00]">WHEN IT COUNTS</span></h2>
            <p className="mb-4">
              This is where the winners actually separate. We looked at <em>when</em> teams made their moves across the season, and the gap is real.
            </p>
            <p className="mb-5">
              Champions made <strong className="text-white">6.95%</strong> of their trades in the playoff stretch, weeks 14 through 18. The rest of the field made just <strong className="text-white">4.14%</strong> of theirs in that window. That's champions being roughly <strong className="text-[#ccff00]">68% more active</strong> in the games-that-matter weeks — the single biggest behavioral gap in the whole dataset.
            </p>

            {/* CHART B */}
            <DTMComparisonBarChart
              data={CHART_B_DATA}
              title="Trade Timing: Champions vs. Field"
              subtitle="Share of each group's trades by season period"
              source="DTM Master Data Export — A3.csv | 167,265 trades"
              seriesAKey="seriesA"
              seriesBKey="seriesB"
              seriesAName="Champions"
              seriesBName="Field"
              seriesAColor="#ccff00"
              seriesBColor="#555"
              height={280}
              formatter={(v) => `${v}%`}
            />

            <p className="mt-5">
              That's the difference between busy and effective. While the field is either checked out or done for the year, champions are still shaping their roster when the playoffs are on the line. They're not trading more all season. They're trading <em>later</em>, when a single roster upgrade decides a title.
            </p>
          </section>

          {/* ── Active Trader ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHAT COUNTS AS AN ACTIVE TRADER?</h2>
            <p className="mb-3">Being "active" isn't about volume — it's about intent. A team that makes three sharp, targeted trades is more active in the way that matters than a team that makes twelve lateral moves out of boredom.</p>
            <p className="mb-3">The settings around you shape how trades happen, so know them cold before the season:</p>
            <ul className="space-y-2 pl-4 list-disc mb-4">
              <li><strong className="text-white">Trade deadlines.</strong> In dynasty, this is the line that separates a win-now push from an offseason retool. Champions were most active right up against it.</li>
              <li><strong className="text-white">Veto systems.</strong> Some leagues let managers or the commissioner veto deals, which changes what offers even get made.</li>
              <li><strong className="text-white">Format.</strong> Superflex, TE premium, and roster size all change what a player is worth — and therefore what a fair trade looks like.</li>
            </ul>
            <p>A practical habit: log every offer you make and receive, even the ones you decline. Over a season it shows you your own patterns — and whether you're trading with purpose or just staying busy.</p>
          </section>

          <ToolCTA
            text="See how your league's trades compare"
            subtext="Run any trade through our calculator backed by 167K real dynasty deals."
            buttonText="TRADE CALCULATOR"
            buttonLink="/trade-calculator"
          />

          {/* ── Types of Trades ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE TYPES OF TRADES THAT ACTUALLY MOVE THE NEEDLE</h2>
            <p className="mb-4">Not every trade does the same job. The ones that show up on winning teams tend to fall into a couple of buckets.</p>

            <h3 className="text-base font-bold text-white mb-2">Consolidation</h3>
            <p className="mb-2">Packaging two good pieces into one great one. This is the move that wins titles, because starting lineups win championships, not bench depth.</p>
            <TradeExample>
              <strong className="text-white not-italic">A championship team, Week 8:</strong> sent Jayden Daniels, Ashton Jeanty, and a 2026 2nd. Got Josh Allen and Jahmyr Gibbs. They turned a wide, talented roster into a top-heavy monster at exactly the right time — and won the title.
            </TradeExample>
            <p className="mb-5">That deal looks almost even on a trade calculator. It wasn't. It concentrated value into two elite starters right before the playoff push — which is precisely the kind of move the timing data says champions make.</p>

            <h3 className="text-base font-bold text-white mb-2">Buy-Low</h3>
            <p className="mb-2">Buying a player whose value is temporarily depressed, before the market corrects.</p>
            <TradeExample>
              <strong className="text-white not-italic">A championship team, early season:</strong> sent T.J. Hockenson and a 2027 3rd. Got Chris Olave. They bought a talented receiver at a discount and folded him into a title run.
            </TradeExample>

            <h3 className="text-base font-bold text-white mb-2">Retool and Rebuild</h3>
            <p>Moving veterans for picks and youth (or the reverse) to match your window. These matter most in dynasty, where timelines create real win-win deals.</p>
          </section>

          {/* ── The Most Surprising Finding ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE MOST SURPRISING FINDING: <span className="text-[#ccff00]">"LOSING" TRADES WIN TITLES</span></h2>
            <p className="mb-4">
              Here's the one that flips conventional wisdom on its head. Everyone wants to "win" every trade — to come out ahead on value. But when we looked at the most lopsided trades in our data, the teams that came out <em>behind</em> on value won championships more often than the teams that came out ahead.
            </p>
            <p className="mb-5">
              In trades with a value gap of 96% or more (the most extreme deals), the side that "lost" on value won the championship <strong className="text-[#ccff00]">10.0%</strong> of the time. The side that "won" the trade on value? Just <strong className="text-white">6.7%</strong>. The pattern holds across every level of lopsidedness we checked.
            </p>

            {/* CHART C */}
            <DTMComparisonBarChart
              data={CHART_C_DATA}
              title="Lopsided Trades: Who Actually Wins the Championship?"
              subtitle="Championship rate by which side got more value in the deal"
              source="DTM Master Data Export — A5.csv | 27,485 lopsided trades (>75% gap)"
              seriesAKey="seriesA"
              seriesBKey="seriesB"
              seriesAName="Value Loser (overpaid)"
              seriesBName="Value Winner (got more)"
              seriesAColor="#ccff00"
              seriesBColor="#ff4d4d"
              height={220}
              formatter={(v) => `${v}%`}
              domain={[5, 11]}
            />

            <p className="mt-5 mb-3">
              That sounds backwards until you think about who makes those trades. The team "overpaying" is usually a <strong className="text-white">contender giving up future value or depth to land the one player who pushes them over the top</strong>. They're not getting fleeced. They're buying a title. The team "winning" the trade is often a rebuilder cashing out, collecting value they won't use for years.
            </p>
            <p>The lesson: <strong className="text-white">stop trying to win every trade on paper.</strong> If a deal that looks like an overpay lands you the player who wins you the league, make it.</p>
          </section>

          <InlineNewsletter context="championship" source="trade-more-win-research" />

          {/* ── Does Trading a Lot Hurt? ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">DOES TRADING A LOT ACTUALLY HURT?</h2>
            <p className="mb-3">
              Not really, and that's worth saying plainly. The 10+ trade teams didn't crater; they posted the <strong className="text-white">highest championship rate</strong> of any group. High volume didn't sink anyone.
            </p>
            <p className="mb-3">
              But the gains are small and they flatten out fast. Most of the improvement shows up just by getting off zero and making a few trades. Push from a handful to double digits and the championship rate barely moves. So the risk of over-trading isn't that it tanks your team — it's that it <strong className="text-white">feels like progress while adding almost nothing</strong>. Every trade is a chance to be wrong about a role, an injury, or a projection. Making a dozen instead of a few just means more chances to be wrong for a payoff the data says is marginal.
            </p>
            <p>The lesson: trade when you have a reason, not when you're bored.</p>
          </section>

          {/* ── Dynasty vs Redraft ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">DYNASTY VS REDRAFT: WHY THE ANSWER SHIFTS</h2>
            <p className="mb-3">
              In redraft, your roster resets every year, so trades are about <em>right now</em>: patching a bye week, buying a playoff-run upgrade, exploiting a hot streak. Waivers and lineup decisions carry more of the load.
            </p>
            <p>
              In dynasty, you keep your players, so every trade is permanent and value has layers — production now versus later, age curves, picks as an asset class, and your window (contend or rebuild). That's why dynasty rooms trade more in general, and why timing your moves to your window matters even more.
            </p>
          </section>

          {/* ── Trading With Purpose ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">TRADING WITH PURPOSE</h2>
            <p className="mb-3">Pull it together and the winning approach is simple to state, harder to do:</p>
            <div className="border border-[#262626] bg-[#171717] p-5 space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">01</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">Don't chase volume.</strong> The data says raw trade count barely matters. Stop trading to feel busy.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">02</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">Trade to your window.</strong> Contender? Consolidate into elite starters. Rebuilding? Turn veterans into youth and picks.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">03</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">Be active when it counts.</strong> Champions separate themselves late in the season. When a roster upgrade can swing a playoff game, that's the moment to move.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">04</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">Have a reason every time.</strong> If you can't name the weakness a trade fixes, it's probably busy work.</span>
              </div>
            </div>
            <p>We analyzed real completed leagues, not projections or expert guesses. And the real leagues say the same thing: winning isn't about trading the most. It's about trading with purpose, at the moment it matters.</p>
          </section>

          {/* ── FAQ ────────────────── */}
          <section className="mt-12 border-t border-[#262626] pt-8">
            <h2 className="font-heading text-2xl text-white uppercase mb-5">FREQUENTLY ASKED QUESTIONS</h2>
            <div className="space-y-5">
              {FAQ_SCHEMA.mainEntity.map((q, i) => (
                <div key={i} className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                  <h3 className="text-sm font-bold text-white mb-2">{q.name}</h3>
                  <p className="text-sm text-[#a3a3a3] leading-relaxed">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* ── Share + Bottom CTA ────────────────── */}
        <div className="mt-10 mb-6">
          <ShareBar title="Do Teams That Trade More Win Fantasy Football?" path="/research/do-teams-that-trade-more-win" />
        </div>
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">SEE HOW YOUR TRADES STACK UP</h3>
          <p className="text-xs text-[#888] mb-4">Run any trade through our tools — backed by 167,265 real dynasty deals.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors" data-testid="cta-trade-calculator">TRADE CALCULATOR</Link>
            <Link to="/research" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1" data-testid="cta-more-research">More Research <ArrowRight size={11} /></Link>
          </div>
        </div>

        {/* ── Footnote ────────────────── */}
        <RelatedResearch slug="do-teams-that-trade-more-win" />
        <p className="mt-6 text-center text-[10px] text-[#444] italic">
          Based on 167,265 trades from 6,976 championship Sleeper dynasty leagues, 2021–2026. Trade values use current FantasyCalc values, not as-of-trade-date values.
        </p>

        {/* ── Footer links ────────────────── */}
        <div className="mt-4 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/research" className="hover:text-[#a3a3a3] no-underline">Research</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/trades" className="hover:text-[#a3a3a3] no-underline">Trade Database</Link>
          <Link to="/research/how-lopsided-is-the-average-trade" className="hover:text-[#a3a3a3] no-underline">Trade Fairness</Link>
        </div>
      </article>
    </div>
  );
}
